import { useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import useSWR from "swr";
import { SpaceType } from "../../../data";
import { formatTime } from "../../time_utils";

export type PortalBookingType = "access" | "membership";

interface Booking {
  id: string;
  isArchived: boolean;
  imageUrl: string;
  date: string;
  time?: string;
  spaceName: string;
  spaceId: string;
  spaceType: string;
  location: string;
  cancellable: boolean;
  isCancelled: boolean;
}

interface UsePortalBookingQueryReturn {
  bookings: Booking[];
  isLoading: boolean;
  handleLoadMore: (() => void) | undefined;
}

export function usePortalBookingQuery(
  partnerId: string,
  bookingState: "upcoming" | "archived",
  bookingType?: PortalBookingType,
  subscriptionId?: string
): UsePortalBookingQueryReturn {
  const [perPage, setPerPage] = useState(15);
  const [bookings, setBookings] = useState<Booking[]>([]);

  const bookingTypeParam = bookingType ? `&booking_type=${bookingType}` : "";
  const subscriptionIdParam = subscriptionId
    ? `&subscription_id=${subscriptionId}`
    : "";
  const { data } = useSWR(
    `/api/v3/portal/partners/${partnerId}/bookings?per_page=${perPage}&booking_state=${bookingState}${bookingTypeParam}${subscriptionIdParam}`
  );

  useEffect(() => {
    if (data) {
      if (bookingType === "access") {
        setBookings(data.data.map(toPortalBooking));
      } else if (bookingType === "membership") {
        setBookings(data.data.map(toMembershipBooking));
      }
    }
  }, [bookingType, data]);

  return {
    bookings,
    isLoading: !data,
    handleLoadMore:
      data?.paging.total_page > 1 ? () => setPerPage((v) => v + 15) : undefined,
  };
}

function toPortalBooking(data: any): Booking {
  const isArchived = data.past || data.canceled;

  return {
    id: data.id,
    imageUrl: data.space.image.url,
    date: toDate(data.start_date, data.end_date, data.date),
    time:
      data.start_time && data.end_time
        ? `${formatTime(data.start_time)} to ${formatTime(data.end_time)}`
        : undefined,
    spaceId: data.space.id,
    spaceName: data.space.name,
    spaceType: toSpaceType(data.space.space_type),
    location: data.location.short_address,
    cancellable: data.cancellation_allowed && !isArchived,
    isCancelled: data.canceled,
    isArchived,
  };
}

function formatDate(date: string) {
  return format(parseISO(date), "EEEE, MMMM dd, yyyy");
}

function toSpaceType(spaceType: SpaceType) {
  if (spaceType === "entire_location") {
    return "Day Pass";
  } else if (spaceType === "meeting_room") {
    return "Meeting Room";
  } else if (spaceType === "private_office") {
    return "Private Office";
  }
  return "";
}

function toDate(startDate: string, endDate: string, date?: string) {
  if (startDate && endDate) {
    return startDate === endDate
      ? formatDate(startDate)
      : `${formatDate(startDate)} to ${formatDate(endDate)}`;
  } else if (date) {
    return formatDate(date);
  }
  return "";
}

function toMembershipBooking(data: any): Booking {
  const memberBookable = data.member_bookable || {};
  const isArchived = memberBookable.past || memberBookable.canceled;

  return {
    id: data.id,
    imageUrl: memberBookable.space?.image.url,
    date: toDate(data.start_date, data.end_date),
    spaceId: memberBookable.space?.id,
    spaceName: memberBookable.location?.name,
    spaceType: toSpaceType(memberBookable.space?.space_type),
    location: memberBookable.location?.short_address,
    cancellable: memberBookable.cancellation_allowed && !isArchived,
    isCancelled: memberBookable.canceled,
    isArchived,
  };
}
